import { memo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { StatCard } from './StatCard';
import { DailyBarChart } from './DailyBarChart';
import { PureRevenueCalculator } from './PureRevenueCalculator';
import { DashboardStatsResponse } from '../../../services/ocpp/types';
import { formatNumber } from '../../../shared/utils/numberFormat';

interface MetricsContentProps {
  stats: DashboardStatsResponse | undefined;
  loading: boolean;
  dateRange: string;
  stationIds?: string;
  organizationIds?: string;
  /** Class on the stat-card grid — `stats-grid` (desktop) or `mobile-stats-grid`. */
  statsGridClassName: string;
  /** Per-layout footer: the desktop table or the mobile metrics list. */
  tableSlot: ReactNode;
  /** Hide mode for this page: masks every figure below. */
  hidden?: boolean;
}

/**
 * Shared metrics body (stat cards + revenue calculator + the tabbed per-day
 * chart + a per-layout table footer). Desktop and mobile layouts render this
 * with only `statsGridClassName` and `tableSlot` differing.
 */
export const MetricsContent = memo(function MetricsContent({
  stats,
  loading,
  dateRange,
  stationIds,
  organizationIds,
  statsGridClassName,
  tableSlot,
  hidden = false,
}: MetricsContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={statsGridClassName}>
        <StatCard
          title={t('dashboard.metrics.totalEnergyDelivered')}
          value={formatNumber(stats?.totalKwh || 0)}
          icon="bolt"
          suffix={` ${t('dashboard.metrics.kwh')}`}
          loading={loading}
          hidden={hidden}
        />
        <StatCard
          title={t('dashboard.metrics.totalRevenue')}
          value={formatNumber(stats?.totalRevenue || 0)}
          icon="payments"
          suffix={` ${t('dashboard.metrics.uah')}`}
          loading={loading}
          hidden={hidden}
        />
        <StatCard
          title={t('dashboard.metrics.registeredUsers')}
          value={formatNumber(stats?.totalUsers || 0)}
          icon="group"
          loading={loading}
          hidden={hidden}
        />
      </div>

      <PureRevenueCalculator
        revenue={stats?.totalRevenue || 0}
        kwh={stats?.totalKwh || 0}
        hidden={hidden}
      />

      <DailyBarChart
        dateRange={dateRange}
        stationIds={stationIds}
        organizationIds={organizationIds}
        hidden={hidden}
      />

      {tableSlot}
    </>
  );
});

export default MetricsContent;
